import React, { useState, useEffect, useRef } from 'react';
import { 
  Box, 
  InputBase, 
  Paper, 
  Typography, 
  CircularProgress,
  Button,
  Avatar,
  Fade,
  Popper,
  ClickAwayListener,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Divider,
  Chip
} from '@mui/material';
import { Search as SearchIcon, Phone as PhoneIcon, Add as AddIcon, AssignmentInd as AssignedIcon } from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { globalSearch } from '../../../api/searchAPI';
import { setSelectedquerie } from '../../../reduxcomponents/slices/queriesSlice';
import { useDispatch } from 'react-redux';

const TopSearch = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const userRole = useSelector((state) => state.tokens?.user?.permission);
  const allowedRoles = ['Admin', 'Admin-a', 'Sales Manager', 'Sales Team Lead', 'Sales Executive', 'Operation Manager'];

  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [searched, setSearched] = useState(false);
  
  const anchorRef = useRef(null);
  const debounceTimer = useRef(null);

  // Only render for allowed roles
  if (!allowedRoles.includes(userRole)) {
    return null;
  }

  const handleSearch = async (phone) => {
    if (!phone || phone.trim().length < 3) {
      setResults([]);
      setSearched(false);
      setOpen(false);
      return;
    }

    setLoading(true);
    setOpen(true);
    setSearched(true);
    
    try {
      const results = await globalSearch(phone);
      setResults(results);
    } catch (error) {
      console.error("Search error:", error);
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const val = e.target.value;
    setSearchTerm(val);

    if (debounceTimer.current) clearTimeout(debounceTimer.current);

    if (val.length > 2) {
      debounceTimer.current = setTimeout(() => {
        handleSearch(val);
      }, 500); // 500ms debounce
    } else {
      setResults([]);
      setSearched(false);
      setOpen(false);
    }
  };

  const handleAddInquiry = () => {
    setOpen(false);
    setSearchTerm('');
    navigate('/createItinerary', { state: { searchTerm } });
  };

  const handleResultClick = (item) => {
    setOpen(false);
    setSearchTerm('');
    if (item.type === 'Query') {
      navigate(`/query?search=${encodeURIComponent(item.phone || item.name || item.quotation_no)}`);
    } else if (item.type === 'Inquiry') {
      navigate('/inquiry', { state: { search: item.phone || item.name } });
    }
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <ClickAwayListener onClickAway={handleClose}>
      <Box sx={{ position: 'relative', mr: 2 }} ref={anchorRef}>
        <Paper
          component="form"
          sx={{
            p: '2px 4px',
            display: 'flex',
            alignItems: 'center',
            width: 280,
            borderRadius: '24px',
            backgroundColor: 'rgba(255, 255, 255, 0.15)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
            transition: 'all 0.3s ease',
            '&:hover, &:focus-within': {
               backgroundColor: 'rgba(255, 255, 255, 0.25)',
               boxShadow: '0 6px 20px rgba(0,0,0,0.15)',
               width: 320
            }
          }}
          onSubmit={(e) => { e.preventDefault(); handleSearch(searchTerm); }}
        >
          <Box sx={{ p: '10px', display: 'flex', alignItems: 'center', color: 'white' }}>
            <SearchIcon />
          </Box>
          <InputBase
            sx={{ ml: 1, flex: 1, color: 'white', '& input::placeholder': { color: 'rgba(255,255,255,0.7)', opacity: 1 } }}
            placeholder="Search name, email, phone..."
            value={searchTerm}
            onChange={handleInputChange}
            inputProps={{ 'aria-label': 'search' }}
            onClick={() => { if(searched) setOpen(true); }}
          />
          {loading && (
             <Box sx={{ p: '10px', display: 'flex', alignItems: 'center' }}>
                <CircularProgress size={20} sx={{ color: 'white' }} />
             </Box>
          )}
        </Paper>

        <Popper
          open={open}
          anchorEl={anchorRef.current}
          placement="bottom-end"
          transition
          disablePortal
          style={{ zIndex: 1300, width: '380px', marginTop: '12px' }}
        >
          {({ TransitionProps }) => (
            <Fade {...TransitionProps} timeout={350}>
              <Paper 
                elevation={8}
                sx={{ 
                  overflow: 'hidden',
                  borderRadius: 3,
                  boxShadow: '0 12px 40px rgba(0,0,0,0.15)',
                  border: '1px solid rgba(255,255,255,0.6)',
                  bgcolor: '#ffffff'
                }}
              >
                {!loading && searched && results.length > 0 && (
                   <List sx={{ p: 0, maxHeight: 350, overflow: 'auto' }}>
                      <Box sx={{ px: 2, py: 1.5, bgcolor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                         <Typography variant="caption" fontWeight="bold" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>
                            Search Results ({results.length})
                         </Typography>
                      </Box>
                      {results.map((item, index) => (
                        <React.Fragment key={item.id + index}>
                          <ListItem onClick={() => handleResultClick(item)} alignItems="flex-start" sx={{ py: 1.5, cursor: 'pointer', '&:hover': { bgcolor: '#f0f7ff' } }}>
                            <ListItemAvatar>
                              <Avatar sx={{ bgcolor: item.type === 'Inquiry' ? '#ede9fe' : '#dcfce7', color: item.type === 'Inquiry' ? '#6d28d9' : '#15803d' }}>
                                 {item.name.charAt(0).toUpperCase()}
                              </Avatar>
                            </ListItemAvatar>
                            <ListItemText
                              primary={
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                                  <Typography variant="subtitle2" fontWeight={700} color="#1e293b">
                                    {item.name}
                                  </Typography>
                                  <Chip 
                                    label={item.type} 
                                    size="small" 
                                    sx={{ 
                                      height: 20, 
                                      fontSize: '0.65rem', 
                                      fontWeight: 600,
                                      bgcolor: item.type === 'Inquiry' ? '#f3e8ff' : '#dcfce7',
                                      color: item.type === 'Inquiry' ? '#7e22ce' : '#15803d'
                                    }} 
                                  />
                                </Box>
                              }
                              secondary={
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#64748b' }}>
                                    <PhoneIcon sx={{ fontSize: 14 }} />
                                    <Typography variant="caption">{item.phone}</Typography>
                                  </Box>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#64748b' }}>
                                    <AssignedIcon sx={{ fontSize: 14 }} />
                                    <Typography variant="caption">Assigned: <strong style={{ color: item.assignedTo !== 'N/A' ? '#0f172a' : 'inherit'}}>{item.assignedTo}</strong></Typography>
                                  </Box>
                                  {item.type === 'Query' && item.quotation_no && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#64748b', mt: 0.5 }}>
                                      <Typography variant="caption" sx={{ bgcolor: '#e2e8f0', px: 1, py: 0.2, borderRadius: 1 }}>Q: {item.quotation_no}</Typography>
                                    </Box>
                                  )}
                                </Box>
                              }
                            />
                          </ListItem>
                          {index < results.length - 1 && <Divider component="li" />}
                        </React.Fragment>
                      ))}
                   </List>
                )}

                {!loading && searched && results.length === 0 && (
                   <Box sx={{ p: 4, textAlign: 'center' }}>
                      <Box sx={{ 
                        width: 60, height: 60, borderRadius: '50%', 
                        bgcolor: '#f1f5f9', display: 'flex', alignItems: 'center', 
                        justifyContent: 'center', mx: 'auto', mb: 2 
                      }}>
                        <PhoneIcon sx={{ fontSize: 30, color: '#94a3b8' }} />
                      </Box>
                      <Typography variant="subtitle1" fontWeight="bold" color="#334155" gutterBottom>
                         No records found
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                         No inquiry or query matches <strong>{searchTerm}</strong>.
                      </Typography>
                      <Button
                        variant="contained"
                        fullWidth
                        startIcon={<AddIcon />}
                        onClick={handleAddInquiry}
                        sx={{
                          borderRadius: 2,
                          py: 1,
                          textTransform: 'none',
                          fontWeight: 600,
                          background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                          boxShadow: '0 4px 12px rgba(37, 99, 235, 0.3)',
                          '&:hover': {
                            background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
                            boxShadow: '0 6px 16px rgba(37, 99, 235, 0.4)',
                          }
                        }}
                      >
                         Create Inquiry
                      </Button>
                   </Box>
                )}
              </Paper>
            </Fade>
          )}
        </Popper>
      </Box>
    </ClickAwayListener>
  );
};

export default TopSearch;
